// src/core/utils/queryParams.ts

export type QueryValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | Date
  | Array<string | number | boolean | Date | null | undefined>;

export type QueryParams = Record<string, QueryValue>;

/**
 * Converts an object into a URL query string, omitting blank values.
 * - undefined / null / "" are removed
 * - arrays repeat the key: tags=a&tags=b
 * - Date becomes ISO string
 * - booleans become "true"/"false"
 */
export function toQueryString(params: QueryParams): string {
  const usp = new URLSearchParams();

  const add = (key: string, value: Exclude<QueryValue, undefined | null | "">) => {
    if (value instanceof Date) {
      usp.append(key, value.toISOString());
      return;
    }
    usp.append(key, String(value));
  };

  for (const [key, raw] of Object.entries(params)) {
    if (raw === undefined || raw === null || raw === "") continue;

    if (Array.isArray(raw)) {
      for (const item of raw) {
        if (item === undefined || item === null || item === "") continue;
        add(key, item as any);
      }
      continue;
    }

    add(key, raw as any);
  }

  return usp.toString();
}

/**
 * Adds a query string to a base URL.
 * If qs is empty, returns base unchanged.
 */
export function withQuery(base: string, params: QueryParams): string {
  const qs = toQueryString(params);
  return qs ? `${base}?${qs}` : base;
}
